import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AdesaoService } from '../../../../services/adesao/adesao.service';
import { BrandService } from '../../../../services/brand/brand.service';
import { ClienteAdesao, DependenteMigracao, DestinoMigracao, ElegibilidadeMigracao, EnvioTermo, OrigemOferta, PagamentoAdesao, TermoAdesao } from '../../../../interfaces/adesao.dto';
import { BrlPipe } from '../../../../utils/brl.pipe';
import { agruparPorFamilia, excedente, familiaDoPlano, GrupoMigracao, precoComDependentes, textoDoMotivo } from '../../../../utils/migracao.util';
import { ERRO_CELULAR, ERRO_EMAIL, formatarDestino, validarCelular, validarEmail } from '../../../../utils/contato-termo.util';
import { termoLiberadoPara } from '../../../../utils/termo-liberado.util';
import { AcoesCobranca, CobrancaSimComponent } from '../cobranca-sim/cobranca-sim.component';
import { AssinaturaAtivada, CORES_PLANO } from '../oferta-sim/oferta-sim.component';

const MOTIVOS_CONHECIDOS = new Set(['origem-bloqueada', 'plano-anual', 'sem-destino', 'periodo-pendente', 'operador-sem-acesso', 'sem-assinatura']);

type Etapa = 'plano' | 'dependentes' | 'termo' | 'cobranca' | 'resultado';
type Canal = 'sms' | 'email';
type Cor = { fundo: string; texto: string; sombra: string };

// Plano sem família conhecida: cartão neutro.
const COR_NEUTRA: Cor = { fundo: '#E9E6EE', texto: '#24292D', sombra: 'rgba(36,41,45,.25)' };

/**
 * "Melhorar plano" do admin: cria a assinatura nova em status 5 e o banco cancela a atual
 * quando ela for paga. Sem pagamento, o paciente segue no plano de hoje.
 */
@Component({
  selector: 'app-melhorar-plano-sim',
  standalone: true,
  imports: [CommonModule, FormsModule, BrlPipe, CobrancaSimComponent],
  templateUrl: './melhorar-plano-sim.component.html',
  styleUrls: ['../oferta-sim/oferta-sim.component.scss', './melhorar-plano-sim.component.scss'],
})
export class MelhorarPlanoSimComponent implements OnChanges, OnDestroy {
  @Input() clinicId = 0;
  @Input() personId = 0;
  @Input() cpf = '';
  @Input() guiche = '';
  @Input() abrirDireto = false;
  @Input() origem: OrigemOferta = null;
  @Output() visivel = new EventEmitter<boolean>();
  @Output() migrado = new EventEmitter<AssinaturaAtivada>();

  assinanteAtivo = false;
  aberto = false;
  carregando = false;
  eleg: ElegibilidadeMigracao | null = null;
  destino: DestinoMigracao | null = null;
  grupos: GrupoMigracao[] = [];
  grupoIdx = 0;
  mantidos = new Set<number>();
  etapa: Etapa = 'plano';
  cliente: ClienteAdesao | null = null;
  termo: TermoAdesao | null = null;
  termoEnviadoPara: { planoId: number; em: Date; canal: Canal; destino: string } | null = null;
  canal: Canal = 'sms';
  pagamento: PagamentoAdesao | null = null;
  faltando: DependenteMigracao[] = [];
  antigaCancelada = false;
  erro = '';
  isEnviando = false;
  isImportando = false;
  baixandoModelo = false;
  verificandoTermo = false;
  readonly DIAS_VENCIMENTO = [5, 10, 15];
  diaVencimento = 10;

  readonly frasesCarregando = ['Buscando planos de upgrade...', 'Comparando com o plano atual...', 'Calculando dependentes...'];
  fraseAtual = 0;
  private girarFrases: any = null;

  // Contato corrigido na hora vale só para o envio do termo; o cadastro não muda.
  contatoEditado: Partial<Record<Canal, string>> = {};
  editandoCanal: Canal | null = null;
  valorEdicao = '';
  erroEdicao = '';

  private timerTermo: any = null;
  private termoAte = 0;

  private carga = 0;
  // Só a resposta mais recente grava o termo (verificação em voo não desfaz a importação).
  private seqTermo = 0;
  private subscriptionId: number | null = null;
  private assinaturaDe: string | null = null;
  private criando: Promise<number> | null = null;
  private valorConfirmado: { id: number; valor: number } | null = null;
  private readonly brl = new BrlPipe();

  constructor(private adesaoService: AdesaoService, private brandService: BrandService) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['personId']) {
      this.carga++;
      this.aberto = false;
      this.pararFrases();
      this.reiniciar();
      this.assinanteAtivo = false;
      if (this.abrirDireto && this.personId) {
        await this.carregar();
        return;
      }
      this.visivel.emit(false);
      if (!this.abrirDireto && this.personId) await this.verificarAssinante();
      return;
    }
    if (changes['abrirDireto']) this.visivel.emit(this.mostraFaixa || this.aberto);
  }

  // O banco decide quem é assinante; a senha pode trazer status velho ou vazio (Express, troca de paciente).
  private async verificarAssinante(): Promise<void> {
    const n = this.carga;
    let ativo = false;
    try {
      ativo = (await this.adesaoService.ativacao(this.personId))?.situacao === 'ativo';
    } catch {
      ativo = false;
    }
    if (n !== this.carga) return;
    this.assinanteAtivo = ativo;
    this.visivel.emit(this.mostraFaixa || this.aberto);
  }

  ngOnDestroy(): void {
    this.carga++;
    this.pararFrases();
    this.pararVerificacaoTermo();
  }

  private iniciarFrases(): void {
    this.pararFrases();
    this.carregando = true;
    this.fraseAtual = 0;
    this.girarFrases = setInterval(() => {
      this.fraseAtual = (this.fraseAtual + 1) % this.frasesCarregando.length;
    }, 1800);
  }

  private pararFrases(): void {
    this.carregando = false;
    if (this.girarFrases) clearInterval(this.girarFrases);
    this.girarFrases = null;
  }

  get nomePlano(): string {
    return this.brandService.nomePlano;
  }

  get mostraFaixa(): boolean {
    return this.assinanteAtivo && !this.aberto && !this.abrirDireto;
  }

  get mostra(): boolean {
    return this.aberto && !!this.eleg;
  }

  get verificando(): boolean {
    return this.aberto && this.carregando && !this.eleg;
  }

  get permiteNovaTentativa(): boolean {
    return !!this.eleg && !this.eleg.pode && !MOTIVOS_CONHECIDOS.has(this.eleg.motivo);
  }

  get removeuDependente(): boolean {
    return this.mantidos.size < (this.eleg?.dependentes.length ?? 0);
  }

  get textoBloqueio(): string | null {
    return this.eleg && !this.eleg.pode ? textoDoMotivo(this.eleg.motivo, this.eleg.atual?.nomePlano) : null;
  }

  // ── Visual ──────────────────────────────────────────────────

  // "SiM+ Black" -> "Black".
  nomeCurto(nome: string): string {
    return (nome ?? '').replace(/^(SiM\+|Super Vida)\s+/i, '');
  }

  corDaFamilia(familia?: string | null): Cor {
    return CORES_PLANO[(familia ?? '').toLowerCase()] ?? COR_NEUTRA;
  }

  get familiaAtual(): string {
    return familiaDoPlano(this.eleg?.atual?.nomePlano);
  }

  get corAtual(): Cor {
    return this.corDaFamilia(this.familiaAtual);
  }

  get grupo(): GrupoMigracao | null {
    return this.grupos[this.grupoIdx] ?? null;
  }

  get corDestino(): Cor {
    return this.corDaFamilia(this.destino?.familia ?? this.grupo?.familia);
  }

  get ciclo(): 'mensal' | 'anual' {
    return this.destino?.intervalo === 12 ? 'anual' : 'mensal';
  }

  formatarCpf(cpf: string): string {
    const d = (cpf ?? '').replace(/\D/g, '');
    return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf;
  }

  // ── Carga ───────────────────────────────────────────────────

  async abrir(): Promise<void> {
    if (this.aberto || this.carregando) return;
    await this.carregar();
  }

  async carregar(): Promise<void> {
    const n = ++this.carga;
    this.aberto = true;
    this.reiniciar();
    this.iniciarFrases();
    this.visivel.emit(true);
    try {
      const eleg = await this.adesaoService.elegibilidadeMigracao(this.clinicId, this.personId);
      if (n !== this.carga) return;
      this.eleg = eleg;
      this.mantidos = new Set(eleg.dependentes.map(d => d.pessoaId));
      if (!eleg.pode) return;
      this.grupos = agruparPorFamilia(eleg.destinos);
      this.escolherGrupo(0);
      if (!this.cpf) return;
      const cpf = this.cpf.replace(/\D/g, '');
      const seq = ++this.seqTermo;
      const [cliente, termo] = await Promise.all([this.adesaoService.cliente(cpf), this.adesaoService.termo(cpf)]);
      if (n !== this.carga) return;
      this.cliente = cliente;
      if (seq === this.seqTermo) this.termo = termo;
      this.canal = cliente?.telefone ? 'sms' : 'email';
    } finally {
      if (n === this.carga) this.pararFrases();
    }
  }

  private reiniciar(): void {
    this.pararVerificacaoTermo();
    this.eleg = null;
    this.destino = null;
    this.grupos = [];
    this.grupoIdx = 0;
    this.etapa = 'plano';
    this.erro = '';
    this.subscriptionId = null;
    this.assinaturaDe = null;
    this.criando = null;
    this.valorConfirmado = null;
    this.cliente = null;
    this.termo = null;
    this.pagamento = null;
    this.faltando = [];
    this.termoEnviadoPara = null;
    this.contatoEditado = {};
    this.editandoCanal = null;
    this.valorEdicao = '';
    this.erroEdicao = '';
  }

  // ── Plano ───────────────────────────────────────────────────

  escolher(destino: DestinoMigracao): void {
    if (this.destino?.opcao.planoId !== destino.opcao.planoId) {
      this.subscriptionId = null;
      this.assinaturaDe = null;
      this.criando = null;
      this.valorConfirmado = null;
      this.pagamento = null;
      this.termoEnviadoPara = null;
      this.pararVerificacaoTermo();
    }
    this.destino = destino;
  }

  // A aba escolhe a família; o ciclo começa no mensal quando existe.
  escolherGrupo(i: number): void {
    const g = this.grupos[i];
    if (!g || (i === this.grupoIdx && this.destino)) return;
    this.grupoIdx = i;
    // Mantém o ciclo escolhido quando o grupo novo tem; senão, mensal e depois anual.
    const mesmoCiclo = this.destino ? (this.ciclo === 'anual' ? g.anual : g.mensal) : null;
    const d = mesmoCiclo ?? g.mensal ?? g.anual;
    if (d) this.escolher(d);
  }

  escolherCiclo(ciclo: 'mensal' | 'anual'): void {
    const d = ciclo === 'mensal' ? this.grupo?.mensal : this.grupo?.anual;
    if (d) this.escolher(d);
  }

  // ── Dependentes ─────────────────────────────────────────────

  irParaDependentes(): void {
    if (!this.destino) return;
    this.etapa = (this.eleg?.dependentes.length ?? 0) > 0 ? 'dependentes' : 'termo';
  }

  alternar(pessoaId: number): void {
    this.mantidos.has(pessoaId) ? this.mantidos.delete(pessoaId) : this.mantidos.add(pessoaId);
  }

  get problemaDependentes(): string | null {
    return this.destino ? excedente(this.destino, this.mantidos.size) : null;
  }

  get precoEstimado(): number {
    return this.destino ? precoComDependentes(this.destino, this.mantidos.size) : 0;
  }

  avancarDependentes(): void {
    if (this.problemaDependentes) return;
    this.etapa = 'termo';
  }

  // ── Termo ───────────────────────────────────────────────────

  readonly canais: { id: Canal; nome: string; semContato: string }[] = [
    { id: 'sms', nome: 'SMS', semContato: 'Sem celular no cadastro' },
    { id: 'email', nome: 'E-mail', semContato: 'Sem e-mail no cadastro' },
  ];

  get termoLiberado(): boolean {
    return termoLiberadoPara(this.termo, this.destino?.opcao.planoId, this.termoEnviadoPara);
  }

  get termoImportado(): EnvioTermo | null {
    const planoId = this.destino?.opcao.planoId;
    return (this.termo?.envios ?? []).find(e => e.planoId === planoId && e.canal === 'importado') ?? null;
  }

  destinoDoCanal(canal: Canal): string {
    return this.contatoEditado[canal] ?? (canal === 'sms' ? this.cliente?.telefone : this.cliente?.email) ?? '';
  }

  formatarDestino(canal: Canal, destino: string): string {
    return formatarDestino(canal, destino);
  }

  editarContato(canal: Canal): void {
    this.editandoCanal = canal;
    this.canal = canal;
    this.valorEdicao = canal === 'sms' ? this.destinoDoCanal(canal).replace(/\D/g, '') : this.destinoDoCanal(canal);
    this.erroEdicao = '';
  }

  salvarContato(): void {
    const canal = this.editandoCanal;
    if (!canal) return;
    const valor = canal === 'sms' ? validarCelular(this.valorEdicao) : validarEmail(this.valorEdicao);
    if (!valor) {
      this.erroEdicao = canal === 'sms' ? ERRO_CELULAR : ERRO_EMAIL;
      return;
    }
    this.contatoEditado[canal] = valor;
    this.editandoCanal = null;
    this.erroEdicao = '';
  }

  cancelarEdicao(): void {
    this.editandoCanal = null;
    this.erroEdicao = '';
  }

  contatoFoiEditado(canal: Canal): boolean {
    return this.contatoEditado[canal] !== undefined;
  }

  async enviarTermo(): Promise<void> {
    const destino = this.destino;
    const canal = this.canal;
    const para = this.destinoDoCanal(canal);
    if (!destino || this.isEnviando) return;
    if (!para) {
      this.erro = canal === 'sms' ? 'Paciente sem celular no cadastro.' : 'Paciente sem e-mail no cadastro.';
      return;
    }
    this.isEnviando = true;
    this.erro = '';
    const n = this.carga;
    const seq = ++this.seqTermo;
    try {
      const termo = await this.adesaoService.enviarTermo({
        cpf: this.cpf, planoId: destino.opcao.planoId, planoNome: destino.opcao.nomePlano,
        paymentMethodCode: 'terminal', clinicId: this.clinicId, canal, destino: para,
      });
      if (n !== this.carga) return;
      if (seq === this.seqTermo) this.termo = termo;
      if (this.destino?.opcao.planoId !== destino.opcao.planoId) return;
      this.termoEnviadoPara = { planoId: destino.opcao.planoId, em: new Date(), canal, destino: para };
      if (!this.termoLiberado) this.iniciarVerificacaoTermo();
    } catch (e: any) {
      if (n === this.carga) this.erro = e?.message ?? 'Não foi possível enviar o termo.';
    } finally {
      this.isEnviando = false;
    }
  }

  // A cada 15 s por até 15 min depois do envio; o botão "Verificar" consulta na hora.
  private iniciarVerificacaoTermo(): void {
    this.pararVerificacaoTermo();
    this.termoAte = Date.now() + 15 * 60 * 1000;
    this.timerTermo = setInterval(() => {
      if (Date.now() > this.termoAte || this.termoLiberado) {
        this.pararVerificacaoTermo();
        return;
      }
      this.verificarTermo(false);
    }, 15_000);
  }

  private pararVerificacaoTermo(): void {
    if (this.timerTermo) clearInterval(this.timerTermo);
    this.timerTermo = null;
  }

  async verificarTermo(manual = true): Promise<void> {
    const cpf = (this.cpf ?? '').replace(/\D/g, '');
    if (this.verificandoTermo || !cpf) return;
    this.verificandoTermo = true;
    const n = this.carga;
    const seq = ++this.seqTermo;
    try {
      const t = await this.adesaoService.termo(cpf);
      if (n !== this.carga || seq !== this.seqTermo) return;
      if (t) this.termo = t;
      if (this.termoLiberado) this.pararVerificacaoTermo();
    } catch {
      if (manual && n === this.carga) this.erro = 'Não foi possível consultar a assinatura agora.';
    } finally {
      this.verificandoTermo = false;
    }
  }

  async baixarTermoModelo(): Promise<void> {
    const planoId = this.destino?.opcao?.planoId;
    if (this.baixandoModelo || !planoId) return;
    this.baixandoModelo = true;
    this.erro = '';
    try {
      await this.adesaoService.baixarTermoModelo(planoId, this.clinicId);
    } catch (e: any) {
      this.erro = e.message;
    } finally {
      this.baixandoModelo = false;
    }
  }

  async importarTermo(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files?.[0];
    input.value = '';
    if (!arquivo || !this.destino || this.isImportando) return;
    if (!(arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf'))) {
      this.erro = 'Selecione o termo assinado em PDF.';
      return;
    }
    if (arquivo.size > 10 * 1024 * 1024) {
      this.erro = 'O PDF passa de 10 MB.';
      return;
    }
    this.isImportando = true;
    this.erro = '';
    const n = this.carga;
    const seq = ++this.seqTermo;
    try {
      const termo = await this.adesaoService.importarTermo(this.cpf.replace(/\D/g, ''), this.destino.opcao.planoId, this.clinicId, arquivo);
      if (n !== this.carga || seq !== this.seqTermo) return;
      this.termo = termo;
      if (this.termoLiberado) this.pararVerificacaoTermo();
    } catch (e: any) {
      if (n === this.carga) this.erro = e?.message ?? 'Não foi possível importar o termo.';
    } finally {
      this.isImportando = false;
    }
  }

  irParaCobranca(): void {
    if (this.termoLiberado) this.etapa = 'cobranca';
  }

  // ── Cobrança ────────────────────────────────────────────────

  get aceitaPix(): boolean {
    return !!this.destino?.opcao.formasPagamento.some(f => f.codigo === 'pix');
  }

  private get mantidosLista(): number[] {
    return [...this.mantidos];
  }

  private chaveMantidos(): string {
    return [...this.mantidos].sort((a, b) => a - b).join(',');
  }

  private diaEscolhido(): number | null {
    return this.destino?.intervalo === 1 && this.destino.opcao.aceitaVencimento ? this.diaVencimento : null;
  }

  // Uma criação por plano+forma; clique duplo espera a mesma promessa.
  private garantirMigracao(codigo: 'terminal' | 'pix'): Promise<number> {
    const destino = this.destino;
    const forma = destino?.opcao.formasPagamento.find(f => f.codigo === codigo);
    if (!destino || !forma) return Promise.reject(new Error(codigo === 'pix' ? 'Este plano não aceita PIX.' : 'Este plano não aceita maquininha.'));
    const chave = `${destino.opcao.planoId}:${forma.id}:${this.chaveMantidos()}`;
    if (this.subscriptionId && this.assinaturaDe === chave) return Promise.resolve(this.subscriptionId);
    if (this.criando && this.assinaturaDe === chave) return this.criando;

    this.assinaturaDe = chave;
    const n = this.carga;
    const obsoleto = () => n !== this.carga || this.assinaturaDe !== chave;
    let p!: Promise<number>;
    p = (async () => {
      try {
        const r = await this.adesaoService.criarMigracao({
          cpf: this.cpf, personId: this.personId, planoId: destino.opcao.planoId,
          paymentMethodId: forma.id, paymentMethodCode: forma.codigo, establishmentId: this.clinicId,
          diaVencimento: this.diaEscolhido(), dependentesMantidos: this.mantidosLista,
          origem: this.origem,
        });
        if (obsoleto()) throw new Error('O paciente ou o plano mudou. Refaça a cobrança.');
        this.subscriptionId = r.subscriptionId;
        const pagamento = await this.adesaoService.cobranca(r.subscriptionId);
        if (obsoleto()) throw new Error('O paciente ou o plano mudou. Refaça a cobrança.');
        this.pagamento = pagamento;
        this.registrar('migracao_cobranca_iniciada');
        return r.subscriptionId;
      } catch (e) {
        if (!obsoleto()) this.assinaturaDe = null;
        throw e;
      } finally {
        if (this.criando === p) this.criando = null;
      }
    })();
    this.criando = p;
    return p;
  }

  get temAssinatura(): boolean {
    return this.subscriptionId != null;
  }

  onMaquininhaSemResposta(): void {
    this.registrar('migracao_cobranca_falhou');
  }

  get valorCobranca(): number {
    return this.pagamento?.valor ?? this.precoEstimado;
  }

  get valorDiferente(): boolean {
    const real = this.pagamento?.valor;
    return real != null && Math.abs(real - this.precoEstimado) >= 0.01;
  }

  readonly acoes: AcoesCobranca = {
    cobrarNoTerminal: async poiid => {
      const id = await this.garantirMigracao('terminal');
      const real = this.pagamento?.valor;
      if (this.valorDiferente && real != null) {
        const c = this.valorConfirmado;
        if (!c || c.id !== id || c.valor !== real) {
          this.valorConfirmado = { id, valor: real };
          throw new Error(`O CartãoSiM calculou ${this.brl.transform(real)} (estimativa ${this.brl.transform(this.precoEstimado)}). Confira com o paciente e toque em cobrar de novo.`);
        }
      }
      await this.adesaoService.cobrarNoTerminal(id, poiid, 1, this.clinicId);
    },
    gerarLink: async () => {
      const id = await this.garantirMigracao('pix');
      const link = await this.adesaoService.linkPagamento(id);
      this.pagamento = { ...link, valor: this.pagamento?.valor ?? link.valor };
      return this.pagamento;
    },
    enviarLink: (url, email, whatsApp) =>
      this.adesaoService.enviarLink(this.subscriptionId!, this.cliente!.id!, url, this.clinicId, email, whatsApp),
    foiPago: () => this.verificarResultado(),
    pagarComCartao: async dados => {
      const destino = this.destino;
      if (!destino?.opcao.cartaoPaymentMethodId) throw new Error('Este plano não aceita cartão de crédito.');
      const n = this.carga;
      const r = await this.adesaoService.criarMigracao({
        cpf: this.cpf, personId: this.personId, planoId: destino.opcao.planoId,
        paymentMethodId: destino.opcao.cartaoPaymentMethodId, paymentMethodCode: 'credit_card',
        establishmentId: this.clinicId, diaVencimento: this.diaEscolhido(), dependentesMantidos: this.mantidosLista,
        parcelas: dados.parcelas, origem: this.origem,
        cartao: { holderName: dados.holderName, brand: dados.brand, checkoutAttemptId: dados.checkoutAttemptId ?? '', ...dados.encrypted },
      });
      if (n !== this.carga || this.destino?.opcao.planoId !== destino.opcao.planoId) {
        throw new Error('O paciente ou o plano mudou. Refaça a cobrança.');
      }
      this.subscriptionId = r.subscriptionId;
      this.assinaturaDe = `${destino.opcao.planoId}:${destino.opcao.cartaoPaymentMethodId}:${this.chaveMantidos()}`;
      this.registrar('migracao_cobranca_iniciada');
    },
  };

  // ── Resultado ───────────────────────────────────────────────

  // Quem migra já é assinante ativo: o sinal de pago é a assinatura nova ativa, não o CS_CUSTOMER.
  async verificarResultado(): Promise<boolean> {
    if (this.etapa === 'resultado') return true;
    const id = this.subscriptionId;
    const atual = this.eleg?.atual;
    if (!id || !atual || !this.destino) return false;
    const n = this.carga;
    const r = await this.adesaoService.resultadoMigracao(id, this.personId, atual.id, this.mantidosLista);
    if (n !== this.carga || !r?.novaAtiva) return false;

    this.etapa = 'resultado';
    this.antigaCancelada = r.antigaCancelada;
    const porId = new Map((this.eleg?.dependentes ?? []).map(d => [d.pessoaId, d]));
    this.faltando = r.dependentesFaltando.map(p => porId.get(p) ?? { pessoaId: p, nome: String(p), cpf: '', telefone: '' });
    this.registrar('migracao_contratada');
    const d = this.destino;
    this.migrado.emit({
      cdConvenio: r.cdConvenio, cdCategoria: r.cdCategoria, comPlano: null,
      familia: { familia: d.familia, nome: d.nome, resumo: d.resumo, beneficios: d.beneficios, beneficiosAusentes: [], notas: [], mensal: null, anual: null } as any,
    });
    return true;
  }

  private registrar(tipo: string): void {
    this.adesaoService.registrarEvento(tipo, this.destino?.familia ?? '', this.personId, this.clinicId, undefined, this.origem);
  }
}
